import { useCallback, useEffect, useRef, useState } from "react";
import { speech, SPEECH_LANG } from "@/lib/speech";

export { SPEECH_LANG };

const WAKE_MS = 30000;

export function useVoice(langCode = "en") {
  const lang = speech.langFor(langCode);
  const [speaking, setSpeaking] = useState(false);
  const [listening, setListening] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [loopOn, setLoopOn] = useState(false);
  const [wake, setWake] = useState(false);
  const [enabled, setEnabled] = useState(() => localStorage.getItem("copilot_voice") !== "0");
  const recRef = useRef(null);
  const loopRef = useRef(false);
  const speakingRef = useRef(false);
  const processingRef = useRef(false);
  const handlerRef = useRef(null);
  const restartTimer = useRef(null);
  const lastHeard = useRef(Date.now());

  const getSR = () => (typeof window !== "undefined" ? (window.SpeechRecognition || window.webkitSpeechRecognition) : null);

  const clearRestart = () => { if (restartTimer.current) { clearTimeout(restartTimer.current); restartTimer.current = null; } };

  const stopRec = useCallback(() => {
    clearRestart();
    const r = recRef.current;
    recRef.current = null;
    if (r) { try { r.onend = null; r.abort(); } catch { /* noop */ } }
    setListening(false);
  }, []);

  const startRec = useCallback(() => {
    const SR = getSR();
    if (!SR || recRef.current || speakingRef.current || processingRef.current) return;
    const rec = new SR();
    rec.lang = lang; rec.interimResults = false; rec.maxAlternatives = 1; rec.continuous = false;
    let started = false;
    const watchdog = setTimeout(() => { if (!started && recRef.current === rec) { try { rec.abort(); } catch { /* noop */ } recRef.current = null; if (loopRef.current) scheduleRestart(1500); } }, 6000);
    rec.onstart = () => { started = true; clearTimeout(watchdog); setListening(true); };
    rec.onresult = async (e) => {
      const t = e.results?.[0]?.[0]?.transcript?.trim();
      if (!t) return;
      lastHeard.current = Date.now(); setWake(false);
      stopRec();
      processingRef.current = true; setProcessing(true);
      try { await handlerRef.current?.(t); } catch { /* handled by caller */ }
      processingRef.current = false; setProcessing(false);
      // If nothing is being spoken, resume listening right away; otherwise the speaking watcher resumes it.
      if (loopRef.current && !speakingRef.current) scheduleRestart(400);
    };
    rec.onerror = (e) => {
      if (e.error === "not-allowed" || e.error === "service-not-allowed") { loopRef.current = false; setLoopOn(false); stopRec(); }
    };
    rec.onend = () => {
      clearTimeout(watchdog);
      recRef.current = null; setListening(false);
      if (loopRef.current && !speakingRef.current && !processingRef.current) scheduleRestart(300);
    };
    recRef.current = rec;
    try { rec.start(); } catch { recRef.current = null; if (loopRef.current) scheduleRestart(1000); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang, stopRec]);

  const scheduleRestart = (ms) => { clearRestart(); restartTimer.current = setTimeout(() => { restartTimer.current = null; if (loopRef.current) startRec(); }, ms); };

  // Pause mic while AI speaks (echo guard); resume when speech ends.
  useEffect(() => speech.subscribe(({ speaking: s }) => {
    speakingRef.current = s; setSpeaking(s);
    if (s) { if (recRef.current) stopRec(); }
    else if (loopRef.current && !processingRef.current) scheduleRestart(350);
  }), [stopRec]); // eslint-disable-line react-hooks/exhaustive-deps

  // Wake prompt after 30s of silence while listening
  useEffect(() => {
    if (!loopOn) { setWake(false); return; }
    const id = setInterval(() => { if (listening && Date.now() - lastHeard.current > WAKE_MS) setWake(true); }, 2000);
    return () => clearInterval(id);
  }, [loopOn, listening]);

  const startLoop = useCallback((onResult) => {
    if (!getSR()) return false;
    handlerRef.current = onResult;
    loopRef.current = true; setLoopOn(true);
    lastHeard.current = Date.now(); setWake(false);
    if (!speakingRef.current) startRec();
    return true;
  }, [startRec]);

  const stopLoop = useCallback(() => {
    loopRef.current = false; setLoopOn(false); setWake(false);
    stopRec();
  }, [stopRec]);

  const nudge = useCallback(() => { lastHeard.current = Date.now(); setWake(false); speech.cancel(); if (loopRef.current) { stopRec(); startRec(); } }, [startRec, stopRec]);

  useEffect(() => () => { loopRef.current = false; stopRec(); }, [stopRec]);

  const stop = useCallback(() => speech.cancel(), []);
  const toggle = () => setEnabled((e) => { localStorage.setItem("copilot_voice", e ? "0" : "1"); if (e) speech.cancel(); return !e; });

  const speak = useCallback((text, { urgent = false, key = null } = {}) => {
    if (!enabled || !text) return false;
    return speech.speak(text, { lang, key, urgent });
  }, [enabled, lang]);

  const listen = useCallback((onResult) => {
    if (!getSR()) return false;
    if (recRef.current) { stopRec(); return true; }
    handlerRef.current = onResult;
    speech.cancel();
    startRec();
    return true;
  }, [startRec, stopRec]);

  const status = speaking ? "speaking" : processing ? "analyzing" : listening ? "listening" : loopOn ? "paused" : "off";

  return { speak, stop, listen, startLoop, stopLoop, nudge, status, wake, loopOn, speaking, listening, processing, enabled, toggle,
           markSpoken: speech.markSpoken, supported: speech.supported, sttSupported: !!getSR() };
}
